import { ContextualInfoButton } from "@/components/buttons/ContextualInfoButton";
import { RiskBandsAdvancedSettings, RiskBandsAdvancedSettingsRefs } from "@/components/layouts/RiskBandsAdvancedSettings";
import { getConfig } from "@/features/branding";
import { useGetToolUsageQuery } from "@/store/services/supabase";
import ScreeningFairUseWarning from "@/tools/risk-bands/components/ScreeningFairUseWarning";
import { ScreeningLimitReview, ScreeningStatsRow } from "@/tools/risk-bands/components/ScreeningLimitReview";
import { RiskBandsAdvancedSettingsValues } from "@/tools/risk-bands/types";
import { Button, Col, Divider, Input, Row, Space, Typography } from "antd";
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
const {
  TextArea
} = Input;
const {
  Text,
  Title
} = Typography;
export interface RiskBandsBatchStep1Refs {
  addresses: MutableRefObject<null>;
  process: MutableRefObject<null>;
  advancedSettings?: RiskBandsAdvancedSettingsRefs;
}
interface RiskBandsBatchStep1Props {
  addresses: string;
  setAddresses: (addresses: string) => void;
  onResetSettings: () => void;
  onProgress: (addresses: string[]) => void;
  advancedSettings: any;
  setAdvancedSettings: Dispatch<SetStateAction<RiskBandsAdvancedSettingsValues>>;
  setRefs: (refs: RiskBandsBatchStep1Refs) => void;
}
export const RiskBandsBatchStep1 = ({
  addresses,
  setAddresses,
  onResetSettings,
  onProgress,
  advancedSettings,
  setAdvancedSettings,
  setRefs
}: RiskBandsBatchStep1Props) => {
  const {
    maxAddresses
  } = getConfig().riskBands.quickBatch;
  const addressesRef = useRef(null);
  const processRef = useRef(null);
  const [settingsRefs, setSettingsRefs] = useState<RiskBandsAdvancedSettingsRefs | undefined>(undefined);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [addressCount, setAddressCount] = useState(0);
  const {
    t
  } = useTranslation();
  const {
    t: wt
  } = useTranslation("widgets", {
    keyPrefix: "textInput.addresses"
  });
  const splitAddresses = (input: string): string[] => {
    return input.split("\n").map(address => address.trim()).filter(address => address !== "");
  };
  const handleProgress = () => {
    onProgress(splitAddresses(addresses));
  };
  useEffect(() => {
    setAddressCount(splitAddresses(addresses).length);
  }, [addresses]);
  useEffect(() => {
    setRefs({
      addresses: addressesRef,
      advancedSettings: settingsRefs,
      process: processRef
    });
  }, [addressesRef, processRef, settingsRefs]);
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  if (!toolUsage) {
    return <></>;
  }
  const currentUsage = toolUsage.singleQuotaMode ? toolUsage.singleQuotaUsage : toolUsage.screening;
  return <Row style={{
    width: "100%",
    gap: "16px"
  }} data-sentry-element="Row" data-sentry-component="RiskBandsBatchStep1" data-sentry-source-file="BatchStep1.tsx">
      <Col flex={1} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
        <Row ref={addressesRef} data-sentry-element="Row" data-sentry-source-file="BatchStep1.tsx">
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            <Title level={5} style={{
            paddingTop: 0,
            margin: " 0 0 16px 0"
          }} data-sentry-element="Title" data-sentry-source-file="BatchStep1.tsx">
              <Space data-sentry-element="Space" data-sentry-source-file="BatchStep1.tsx">
                Enter Addresses <ContextualInfoButton category="guide" id="enteringLocationsAndAddresses" data-sentry-element="ContextualInfoButton" data-sentry-source-file="BatchStep1.tsx" />
              </Space>
            </Title>
            <TextArea aria-label="Addresses" placeholder={wt("placeholder", {
            count: maxAddresses
          }) as string} onChange={event => setAddresses(event.target.value)} rows={20} style={{
            resize: "none"
          }} value={addresses} data-sentry-element="TextArea" data-sentry-source-file="BatchStep1.tsx" />
          </Col>
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            <Text aria-label="Addresses Status" type={addressCount > maxAddresses ? "danger" : "secondary"} data-sentry-element="Text" data-sentry-source-file="BatchStep1.tsx">
              {wt(addressCount > maxAddresses ? "countOverLimit" : "countWithinLimit", {
              count: addressCount,
              max: maxAddresses
            })}
            </Text>
          </Col>
        </Row>
      </Col>
      <Col flex={0} style={{
      width: "100%",
      maxWidth: "300px"
    }} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
        <Row data-sentry-element="Row" data-sentry-source-file="BatchStep1.tsx">
          <Col span={24} style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 0,
          marginBottom: 8
        }} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            <Title level={5} style={{
            paddingTop: 0,
            margin: 0
          }} data-sentry-element="Title" data-sentry-source-file="BatchStep1.tsx">
              Settings
            </Title>
            <Button type="text" onClick={onResetSettings} data-sentry-element="Button" data-sentry-source-file="BatchStep1.tsx">
              Reset
            </Button>
          </Col>
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            <RiskBandsAdvancedSettings setRefs={setSettingsRefs} notice="Quick Batch applies the same settings to all supplied addresses." values={advancedSettings} setValues={setAdvancedSettings} data-sentry-element="RiskBandsAdvancedSettings" data-sentry-source-file="BatchStep1.tsx" />
          </Col>
          <Divider style={{
          marginTop: 12,
          marginBottom: 12
        }} data-sentry-element="Divider" data-sentry-source-file="BatchStep1.tsx" />
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep1.tsx">
            {currentUsage && <>
                <ScreeningFairUseWarning currentUsage={currentUsage} hideDetail />
                <ScreeningLimitReview isOpen={isReviewModalOpen} title="Quick Batch Screening" uploadedAssetCount={addressCount} continueText={t("actions:process")} onBack={() => setIsReviewModalOpen(false)} onContinue={() => {
              setIsReviewModalOpen(false);
              handleProgress();
            }}>
                  <ScreeningStatsRow title="Addresses">
                    <TextArea rows={addressCount > 1 ? 3 : 1} readOnly value={addresses} style={{
                  resize: "none"
                }} />
                  </ScreeningStatsRow>
                  <ScreeningStatsRow title="Archetype">{advancedSettings.archetype ?? ""}</ScreeningStatsRow>
                  <ScreeningStatsRow title="Build Year">{advancedSettings.buildYear.amount}</ScreeningStatsRow>
                  <ScreeningStatsRow title="Floor Height Above Ground">
                    {advancedSettings.heightAboveGround.amount}
                  </ScreeningStatsRow>
                  <ScreeningStatsRow title="Total assets">{addressCount}</ScreeningStatsRow>
                </ScreeningLimitReview>
              </>}
            <Button block type="primary" disabled={addressCount === 0 || addressCount > maxAddresses} onClick={() => setIsReviewModalOpen(true)} data-sentry-element="Button" data-sentry-source-file="BatchStep1.tsx">
              Review
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>;
};